import {
  isLogisticsStaleRefusal,
  LOGISTICS_REVISION_HEADER,
  LogisticsRevisionResponseSchema,
  parseRevisionHeader,
} from '../../logistics/index.js';
import { UnauthorizedError } from './errors.js';
import type { AuthedFetch } from './transport.js';

/** The Logistics page's hold on its site's revision (spec
 *  2026-09-29-logistics-refresh-to-edit). */
export interface ILogisticsRevisionHandle {
  /** The revision the page's data stands at. */
  current(): number;
  /** A write answered: the server's new revision is the page's now. */
  adopt(revision: number): void;
  /** Bracket a write, so a poll answered mid-flight is not read as someone
   *  else's change. */
  begin(): void;
  end(): void;
  /** A write was refused as stale. */
  markStale(): void;
  /** Run a write after the previous one settled (either way), so a write
   *  reads the revision the one before it adopted. */
  run<T>(task: () => Promise<T>): Promise<T>;
}

/** The server refused a write made on top of data that changed since the
 *  page loaded — the refresh dock, not a toast, tells the user. */
export class LogisticsStaleError extends Error {
  constructor() {
    super('logistics changed since this page loaded');
    this.name = 'LogisticsStaleError';
  }
}

/** True for the refusal the refresh dock announces, so no host adds an error toast
 *  of its own on top. */
export function isStaleRefusal(error: unknown): boolean {
  return error instanceof LogisticsStaleError;
}

/** How long a queued write may wait for its answer. `authedFetch` has no
 *  timeout, and a write that never settles would hold every write behind it
 *  and keep the poll from ever locking. */
export const LOGISTICS_WRITE_TIMEOUT_MS = 30_000;

/** The caller's signal and the write's own timeout as one signal — by hand,
 *  since `AbortSignal.any` is not on every JS engine the clients run on. */
function withWriteTimeout(
  caller: AbortSignal | null | undefined,
): { signal: AbortSignal; timedOut: () => boolean; clear: () => void } {
  const controller = new AbortController();
  let didTimeOut = false;
  const timer = setTimeout(() => {
    didTimeOut = true;
    controller.abort(new Error(`logistics write timed out after ${LOGISTICS_WRITE_TIMEOUT_MS}ms`));
  }, LOGISTICS_WRITE_TIMEOUT_MS);
  let detachCaller = () => {};
  if (caller) {
    const forward = () => controller.abort(caller.reason);
    if (caller.aborted) forward();
    else {
      caller.addEventListener('abort', forward, { once: true });
      detachCaller = () => caller.removeEventListener('abort', forward);
    }
  }
  return {
    signal: controller.signal,
    timedOut: () => didTimeOut,
    clear: () => {
      clearTimeout(timer);
      detachCaller();
    },
  };
}

/** The revision-carrying write wrapper over an injected transport. */
export function createLogisticsWrite(authedFetch: AuthedFetch) {
  /** A logistics write. With a handle (the Logistics page) it carries the
   *  page's revision and adopts the answer; with `null` (the scheduler's Stock
   *  tab, outside the page) it is the plain write it always was. */
  async function sendLogisticsWrite(
    url: string,
    init: RequestInit,
    revision: ILogisticsRevisionHandle | null,
  ): Promise<Response> {
    if (!revision) return authedFetch(url, init);
    // Bracketed from the call, not from the turn: a queued write is in flight too.
    revision.begin();
    try {
      return await revision.run(async () => {
        // Read inside the turn — after the write before it adopted its answer.
        const headers = new Headers(init.headers);
        headers.set(LOGISTICS_REVISION_HEADER, String(revision.current()));
        // Timed from the turn, not the call: a write waiting its turn is not slow.
        const timeout = withWriteTimeout(init.signal);
        try {
          const res = await authedFetch(url, { ...init, headers, signal: timeout.signal });
          const next = parseRevisionHeader(res.headers.get(LOGISTICS_REVISION_HEADER));
          if (next !== null) revision.adopt(next);
          if (
            res.status === 409 &&
            isLogisticsStaleRefusal(
              await res
                .clone()
                .json()
                .catch(() => null),
            )
          ) {
            revision.markStale();
            throw new LogisticsStaleError();
          }
          return res;
        } catch (error) {
          // The abort cut off OUR wait, not the server's work: the write may
          // have committed. Mark the page stale so it reconciles before the
          // user retries — a blind retry of a create would duplicate it.
          if (timeout.timedOut()) revision.markStale();
          throw error;
        } finally {
          timeout.clear();
        }
      });
    } finally {
      revision.end();
    }
  }

  async function fetchLogisticsRevision(siteId: string, signal?: AbortSignal): Promise<number> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/logistics/revision`, { signal });
    if (res.status === 401) throw new UnauthorizedError();
    if (!res.ok) throw new Error(`fetch logistics revision failed: ${res.status}`);
    return LogisticsRevisionResponseSchema.parse(await res.json()).revision;
  }

  return { sendLogisticsWrite, fetchLogisticsRevision };
}
